import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'primeicons/primeicons.css';

// MUI Components
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  TableCell,
  TableContainer,
  Toolbar,
  Typography,
  Paper,
  Table,
  Chip,
  TableRow,
  TableHead,
  TableBody,
} from '@mui/material';

// MUI Icons
import {
  AccountCircle,
  DarkModeOutlined as DarkModeOutlinedIcon,
  LightModeOutlined as LightModeOutlinedIcon
} from '@mui/icons-material';
import Logo from '../Logo';

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Utils
import { fetchWrapper, SERVER } from '../../utils/helpers';
import { useReport } from '../../contexts/ReportContext';

function Navbar(props) {
  const { toggleTheme, theme, isDarkMode } = useTheme();
  const { isAuthSU, logout, logoutSU, user, userSU, password, encodedAuth } = useAuth();
  const { setIsDataLoaded } = useReport();
  const [anchorEl, setAnchorEl] = useState(null);
  const [returnsD, setReturnsD] = useState([]);
  const [ordersD, setOrdersD] = useState([]);
  const navigate = useNavigate();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLogout = () => {
    handleClose();
    if (isAuthSU) {
      logoutSU();
      logout();
    } else {
      logout();
    }
    setIsDataLoaded("INITIALIZE");
    navigate('/')
  };

  const getReturns = async () => {
    handleClose();
    try {
      const data = await fetchWrapper(`${SERVER}/get_returns`, user, password, encodedAuth)
      setReturnsD(data?.returnOrders ?? [])
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const getOrders = async () => {
    handleClose();
    try {
      const data = await fetchWrapper(`${SERVER}/get_orders`, user, password, encodedAuth);
      setOrdersD(data?.list?.elements?.order ?? []); // TODO
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const navbarBtnStyle = {
    my: 3,
    mx: 1,
    borderRadius: 20,
    color: theme.navbar.text,
    display: 'block',
    fontFamily: theme.fontFamily,
    fontSize: 14
  }

  const navbarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.color.primary,
    height: '65px',
    zIndex: 100000,
    display: 'flex',
    justifyContent: 'center'
  }

  const menuItem = {
    fontFamily: theme.fontFamily,
    color: theme.text.primary,
    '&.Mui-selected': {
      backgroundColor: `${theme.background.secondary}`,
      '&:hover': {
        backgroundColor: `${theme.background.secondary}`,
      },
    },
    '&:hover': {
      backgroundColor: theme.background.primary,
    }
  }

  // Helper functions (add these before your component)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'REFUND_COMPLETED':
        return 'success';
      case 'IN_TRANSIT':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <AppBar position="static" sx={navbarStyle}>
      <Container
        disableGutters
        sx={{
          maxWidth: '100% !important',
          '@media (min-width: 1200px)': {
            maxWidth: '100% !important'
          }
        }}
      >
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          {/* Logo - Left side */}
          <Box sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => navigate('/')}>
            <Logo fill={theme.text.negative} width="37px" height="47px" background={theme.color.primary} />
            {!props.isMobile && (
              <Typography sx={{ color: theme.text.negative, fontFamily: theme.fontFamily, ml: 1, fontSize: '26px' }}>Inventory Falcon</Typography>
            )}
          </Box>

          {/* Right side group */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              sx={{ fontFamily: theme.fontFamily, color: theme.navbar.text, }}
              onClick={() => navigate('/pricing')}
            // variant="outlined"
            >
              PRICING
            </Button>
            <IconButton
              size="medium"
              onClick={() => window.open("https://www.facebook.com/share/g/yhAdje1Da4dQFR2A/", "_blank")}
              color="inherit"
              sx={{
                backgroundColor: theme.navbar.text,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                transition: "all 0.7s ease",
                '&:hover': {
                  backgroundColor: "rgb(8, 102, 255)", // Discord brand color
                  transform: "scale(1.05)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                  '& .pi': {
                    color: theme.navbar.text, // now this will apply
                  }
                },
                '& .pi': {
                  color: "rgb(8, 102, 255)",
                  fontSize: 20,
                }
              }}
            >
              <i className="pi pi-facebook" />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => window.open("https://discord.gg/9VfPNDyM9h", "_blank")}
              color="inherit"
              sx={{
                backgroundColor: theme.navbar.text,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                transition: "all 0.3s ease",
                '&:hover': {
                  backgroundColor: "rgb(114,137,218)", // Discord brand color
                  transform: "scale(1.05)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                  '& .pi': {
                    color: theme.navbar.text, // now this will apply
                  }
                },
                '& .pi': {
                  color: "rgb(114,137,218)",
                  fontSize: 20,
                }
              }}
            >
              <i className="pi pi-discord" />
            </IconButton>

            {/* Account/Login section */}
            {!!user || !!userSU ? (
              <>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircle style={{ color: theme.navbar.text, fontSize: 40 }} />
                </IconButton>
                <Menu
                  sx={{ zIndex: 1000000, }}
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: theme.background.secondary,
                        color: theme.text.primary,
                      },
                    }
                  }}
                >
                  {!!user && <MenuItem sx={menuItem} onClick={() => { handleClose(); navigate('/account') }}>Account details</MenuItem>}
                  {!user && isAuthSU && <MenuItem sx={menuItem} onClick={() => { handleClose(); navigate('/super-user'); }}>Super User Dashboard</MenuItem>}
                  {!!user && user.toLowerCase() === "tracey.shelley@gmail.com" && <MenuItem sx={menuItem} onClick={getReturns}>See Returns</MenuItem>}
                  {!!user && user.toLowerCase() === "tracey.shelley@gmail.com" && <MenuItem sx={menuItem} onClick={getOrders}>See Orders</MenuItem>}
                  <MenuItem sx={menuItem} onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                sx={{ ...navbarBtnStyle, px: 4, color: theme.color.primary, backgroundColor: theme.navbar.text }}
                onClick={() => navigate('/login')}
                variant="contained"
              >
                LOGIN
              </Button>
            )}

            {/* Theme switch */}
            <IconButton
              onClick={toggleTheme}
              color="inherit"
            >
              {isDarkMode ? <LightModeOutlinedIcon style={{ color: theme.accent.warning, fontSize: 25 }} /> : <DarkModeOutlinedIcon style={{ color: theme.text.primary, fontSize: 25 }} />}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {returnsD.length > 0 && <Modal open={returnsD.length > 0} onClose={() => setReturnsD([])}>
        <Box sx={{
          position: 'absolute',
          top: '53%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '90%',
          maxHeight: '80vh',
          bgcolor: theme.background.primary,
          border: `2px solid ${theme.border.medium}`,
          boxShadow: 24,
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h6" color={theme.color.primary} fontWeight={700} mb={2} fontFamily={theme.fontFamily}>
            Your Returns
          </Typography>

          <TableContainer component={Paper} sx={{ my: 2, maxHeight: "70vh", overflowY: "scroll" }}>
            <Table sx={{ minWidth: 650 }} aria-label="return orders table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Return Order ID</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Unit Price</strong></TableCell>
                  <TableCell><strong>Total Refund</strong></TableCell>
                  <TableCell><strong>Return Reason</strong></TableCell>
                  <TableCell><strong>Refund Status</strong></TableCell>
                  <TableCell><strong>Delivery Status</strong></TableCell>
                  <TableCell><strong>Return Date</strong></TableCell>
                  <TableCell><strong>Return By</strong></TableCell>
                  <TableCell><strong>Channel</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returnsD.map((row) => {
                  // Extract nested data properly
                  const firstOrderLine = row.returnOrderLines?.[0] || {};
                  const productName = firstOrderLine.item?.productName || 'N/A';
                  const sku = firstOrderLine.item?.sku || 'N/A';
                  const quantity = firstOrderLine.quantity?.measurementValue || 0;
                  const unitPrice = firstOrderLine.unitPrice?.currencyAmount || 0;
                  const totalRefund = row.totalRefundAmount?.currencyAmount || 0;
                  const returnReason = firstOrderLine.returnReason || 'N/A';
                  const refundStatus = firstOrderLine.currentRefundStatus || 'N/A';
                  const deliveryStatus = firstOrderLine.currentDeliveryStatus || 'N/A';
                  const returnDate = formatDate(row.returnOrderDate);
                  const returnByDate = formatDate(row.returnByDate);

                  return (
                    <TableRow
                      key={row.returnOrderId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {row.returnOrderId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {row.customerName.firstName} {row.customerName.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Order: {row.customerOrderId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {sku}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{quantity}</TableCell>
                      <TableCell>{formatCurrency(unitPrice)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(totalRefund)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {returnReason.toLowerCase().replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={refundStatus.replace(/_/g, ' ')}
                          color={getStatusColor(refundStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deliveryStatus.replace(/_/g, ' ')}
                          color={getStatusColor(deliveryStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{returnDate}</TableCell>
                      <TableCell>{returnByDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.returnChannel.channelName.replace(/_/g, ' ')}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" color="primary" onClick={() => setReturnsD([])} sx={{ fontFamily: theme.fontFamily }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>}

      {ordersD.length > 0 && <Modal open={ordersD.length > 0} onClose={() => setOrdersD([])}>
        <Box sx={{
          position: 'absolute',
          top: '53%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '95%',
          maxHeight: '80vh',
          bgcolor: theme.background.primary,
          border: `2px solid ${theme.border.medium}`,
          boxShadow: 24,
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h6" color={theme.color.primary} fontWeight={700} mb={2} fontFamily={theme.fontFamily}>
            Your Orders
          </Typography>

          <TableContainer component={Paper} sx={{ my: 2, maxHeight: "70vh", overflowY: "scroll" }}>
            <Table sx={{ minWidth: 800 }} aria-label="orders table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Order ID</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Item Price</strong></TableCell>
                  <TableCell><strong>Tax</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Fulfillment</strong></TableCell>
                  <TableCell><strong>Order Date</strong></TableCell>
                  <TableCell><strong>Est. Delivery</strong></TableCell>
                  <TableCell><strong>Shipping Address</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordersD.map((order) => {
                  // Extract nested data properly
                  const firstOrderLine = order.orderLines?.orderLine?.[0] || {};
                  const productName = firstOrderLine.item?.productName || 'N/A';
                  const sku = firstOrderLine.item?.sku || 'N/A';
                  const condition = firstOrderLine.item?.condition || 'N/A';
                  const quantity = firstOrderLine.orderLineQuantity?.amount || 0;
                  const itemPrice = firstOrderLine.charges?.charge?.[0]?.chargeAmount?.amount || 0;
                  const tax = firstOrderLine.charges?.charge?.[0]?.tax?.taxAmount?.amount || 0;
                  const status = firstOrderLine.orderLineStatuses?.orderLineStatus?.[0]?.status || 'N/A';
                  const fulfillmentOption = firstOrderLine.fulfillment?.fulfillmentOption || 'N/A';
                  const shipMethod = firstOrderLine.fulfillment?.shipMethod || 'N/A';
                  const orderDate = formatDate(order.orderDate);
                  const estimatedDelivery = formatDate(order.shippingInfo?.estimatedDeliveryDate);
                  const shippingAddress = order.shippingInfo?.postalAddress;

                  return (
                    <TableRow
                      key={order.customerOrderId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {order.customerOrderId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PO: {order.purchaseOrderId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {shippingAddress?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customerEmailId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 250 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {sku}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Condition: {condition}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{quantity}</TableCell>
                      <TableCell>{formatCurrency(itemPrice)}</TableCell>
                      <TableCell>{formatCurrency(tax)}</TableCell>
                      <TableCell>
                        <Chip
                          label={status}
                          color={getStatusColor(status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {fulfillmentOption}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {shipMethod}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{orderDate}</TableCell>
                      <TableCell>{estimatedDelivery}</TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {shippingAddress?.address1}
                          </Typography>
                          {shippingAddress?.address2 && (
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {shippingAddress.address2}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {shippingAddress?.city}, {shippingAddress?.state} {shippingAddress?.postalCode}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" color="primary" onClick={() => setOrdersD([])} sx={{ fontFamily: theme.fontFamily }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>}
    </AppBar>
  );
}

export default Navbar;
